import { useState } from "react";
import { ArrowRight, Sparkles, Zap } from "lucide-react";
import { SpendFilterSection } from "../components/dashboard/SpendFilterSection";

type Tab = "Opportunity Portfolio" | "Prioritization" | "Savings Engine" | "Recommendations" | "Execution & Realization";
type ChipFilter = "All" | "Cost" | "Consolidation" | "Maverick" | "Working Capital";
type Status = "Approved" | "In Progress" | "Identified";

const TABS: Tab[] = ["Opportunity Portfolio", "Prioritization", "Savings Engine", "Recommendations", "Execution & Realization"];
const HERO_TABS = ["Who", "What", "Where", "When", "How"];
const CHIPS: ChipFilter[] = ["All", "Cost", "Consolidation", "Maverick", "Working Capital"];

const HERO_STATS = [
  { value: "$6.6M", label: "gross identified", accent: false },
  { value: "$4.7M", label: "net after overlap", accent: true },
  { value: "$0.0M", label: "realized to date", accent: false },
];


const PORTFOLIO_KPIS = [
  { label: "Gross identified", value: "$6.84M", sub: "7 levers, pre-overlap", color: "#f5b800" },
  { label: "Net identified", value: "$4.72M", sub: "after overlap", color: "#ec1c8c" },
  { label: "Working capital freed", value: "$1.32M", sub: "45-day DPO", color: "#14c8b4" },
  { label: "Realized", value: "$0.0M", sub: "tracking begins Q3", color: "#d1d5db" },
];

const OPPORTUNITIES: {
  tag: string;
  title: string;
  value: string;
  valueSub: string;
  addressable: string;
  capture: string;
  priority: string;
  trace: string;
  initials: string;
  owner: string;
  ownerColor: string;
  status: Status;
  category: Exclude<ChipFilter, "All">;
}[] = [
  { tag: "Should-cost gap recovery", title: "Close price-to-should-cost gaps on oil, brakes, bearings", value: "$1.84M", valueSub: "est. annual savings", addressable: "$13.6M", capture: "14%", priority: "P1", trace: "Should-Cost & Price", initials: "ST", owner: "Strategic Sourcing", ownerColor: "#6d28d9", status: "Approved", category: "Cost" },
  { tag: "Duplicate vendor & tail consolidation", title: "Merge 4 vendor clusters, aggregate 12 tail suppliers into catalogs", value: "$1.46M", valueSub: "est. annual savings", addressable: "$11.2M", capture: "13%", priority: "P1", trace: "Tail & Fragmentation", initials: "PR", owner: "Procurement Ops", ownerColor: "#ec4899", status: "In Progress", category: "Consolidation" },
  { tag: "Contract compliance, Maverick", title: "Route 33% non-contracted spend through preferred suppliers", value: "$1.59M", valueSub: "est. annual savings", addressable: "$15.9M", capture: "10%", priority: "P1", trace: "Maverick & Compliance", initials: "CO", owner: "Compliance", ownerColor: "#7c4dab", status: "Identified", category: "Maverick" },
  { tag: "Payment term extension to 45d", title: "Standardize core-supplier DPO 38.4 to 45 days", value: "$1.32M", valueSub: "working capital", addressable: "$48.2M", capture: "WC", priority: "P1", trace: "Payment & Working Capital", initials: "TR", owner: "Treasury & AP", ownerColor: "#0ea5e9", status: "In Progress", category: "Working Capital" },
  { tag: "Pre-peak RFQ, top 5 categories", title: "Lock indexed pricing before the Q1-Q2 spike (60% of spend)", value: "$1.21M", valueSub: "est. annual savings", addressable: "$25.9M", capture: "5–9%", priority: "P1", trace: "Spend Trend", initials: "ST", owner: "Strategic Sourcing", ownerColor: "#6d28d9", status: "Identified", category: "Cost" },
  { tag: "Supplier dual-sourcing", title: "Onboard 2 alternates each for Castrol, Magna (>15% dependency)", value: "Risk down", valueSub: "est. annual savings", addressable: "$16.6M", capture: "–", priority: "P1", trace: "Dependency", initials: "ST", owner: "Strategic Sourcing", ownerColor: "#6d28d9", status: "Identified", category: "Consolidation" },
  { tag: "Dynamic discount capture", title: "Adopt 2/10 Net 30 on discount-eligible suppliers", value: "$0.42M", valueSub: "est. annual savings", addressable: "$15.9M", capture: "2%", priority: "P2", trace: "Payment & Working Capital", initials: "TR", owner: "Treasury & AP", ownerColor: "#0ea5e9", status: "Identified", category: "Working Capital" },
];

const KANBAN = [
  { title: "Identified", dot: "#9ca3af", variant: "identified", cards: [
    { title: "Contract compliance", value: "$1.59M", meta: "Compliance · Q3" },
    { title: "Pre-peak RFQ", value: "$1.21M", meta: "Sourcing · <=90d" },
    { title: "Dual-sourcing", value: "Risk down", meta: "Sourcing · Q4" },
    { title: "Dynamic discount", value: "$0.42M", meta: "Treasury · 4mo" },
  ] },
  { title: "Approved", dot: "#6d28d9", variant: "identified", cards: [
    { title: "Should-cost recovery", value: "$1.84M", meta: "Strategic Sourcing · <=90d" },
  ] },
  { title: "In progress", dot: "#3b82f6", variant: "progress", cards: [
    { title: "Vendor & tail consolidation", value: "$1.46M", meta: "Procurement Ops · <=60d" },
    { title: "Payment terms to 45d", value: "$1.32M WC", meta: "Treasury & AP · 6–9mo" },
  ] },
  { title: "Realized", dot: "#10b981", variant: "realized", cards: [] },
];

const FUNNEL = [
  { label: "Identified", value: "$6.84M", pct: 100, color: "#6d28d9", note: "7 opportunities" },
  { label: "Approved", value: "$1.84M", pct: 27, color: "#4c1d95", note: "1 opportunity" },
  { label: "In progress", value: "$2.78M", pct: 41, color: "#1e1b4b", note: "2 opportunities" },
  { label: "Realized", value: "$0.0M", pct: 3, color: "#10b981", note: "tracking from Q3" },
];

const QUADRANTS = [
  { key: "first", title: "Do first", sub: "Quick, high-return wins", hint: "high value, low effort", items: [["Vendor & tail consolidation", "$1.46M"], ["Payment term to 45d", "$1.32M WC"]] },
  { key: "plan", title: "Plan", sub: "Strategic, multi-quarter", hint: "high value, high effort", items: [["Should-cost gap recovery", "$1.84M"], ["Contract compliance", "$1.59M"]] },
  { key: "easy", title: "Easy wins", sub: "Schedule opportunistically", hint: "low value, low effort", items: [["Dynamic discount", "$0.42M"]] },
  { key: "defer", title: "Defer", sub: "Revisit later", hint: "low value, high effort", items: [["Supplier dual-sourcing", "Risk down"]] },
];

const RECO_KPIS = [
  { label: "Total identified, gross", value: "$6.84M", sub: "6 active levers", color: "#f5b800", accent: false },
  { label: "P1 do first", value: "5", sub: "$6.4M + $1.3M WC", color: "#ec1c8c", accent: true },
  { label: "P2 plan next", value: "1", sub: "$0.42M", color: "#7c3aed", accent: false },
  { label: "Net of overlap", value: "$4.72M", sub: "realizable", color: "#d1d5db", accent: false },
];

const ACTIONS = [
  { group: "P1 · Do first", tone: "p1", items: [
    { title: "Recover should-cost gaps", desc: "Oil, brakes, bearings run 8–17% above modelled should-cost. Re-tender with the buildup as the negotiation baseline.", value: "$1.84M", owner: "Strategic Sourcing · <=90d" },
    { title: "Consolidate duplicate vendors & tail", desc: "4 clusters plus 12 tail suppliers fragment leverage. Merge into catalogs and panels.", value: "$1.46M", owner: "Procurement Ops · <=60d" },
    { title: "Strengthen contract compliance", desc: "33% non-contracted plus 26.6% ad-hoc. Default to panel/catalog, enforce no-PO/no-pay.", value: "$1.59M", owner: "Compliance · Q3" },
    { title: "Extend payment terms to 45 days", desc: "DPO at 38.4d lags best practice. Segment by risk, launch dynamic-discount portal.", value: "$1.32M WC", owner: "Treasury & AP · 6–9mo" },
    { title: "Mandate dual sourcing", desc: "Castrol (19%) and Magna (15%) exceed dependency threshold. Onboard 2 alternates each.", value: "Risk down", owner: "Strategic Sourcing · Q4" },
  ] },
  { group: "P2 · Plan next", tone: "p2", items: [
    { title: "Dynamic discount capture", desc: "Adopt 2/10 Net 30 on discount-eligible suppliers. Low effort, modest return.", value: "$0.42M", owner: "Controller · Q3" },
  ] },
];

const WATERFALL = [
  { label: "Should-Cost", from: 0, to: 1.84, color: "#4c1d95", text: "$1.84M" },
  { label: "Vendor+Tail", from: 1.84, to: 3.3, color: "#6d28d9", text: "$1.46M" },
  { label: "Maverick", from: 3.3, to: 4.89, color: "#7c3aed", text: "$1.59M" },
  { label: "Pre-Peak RFQ", from: 4.89, to: 6.1, color: "#7c3aed", text: "$1.21M" },
  { label: "Discount", from: 6.1, to: 6.52, color: "#1e1b4b", text: "$0.42M" },
  { label: "Overlap", from: 4.4, to: 6.52, color: "#c4b5fd", text: "−$2.12M" },
  { label: "Net P&L", from: 0, to: 4.4, color: "#ec1c8c", text: "$4.40M" },
];

const LEVER_ROWS = [
  { lever: "Should-Cost Gap Recovery", addressable: "$13.6M", capture: "14%", value: "$1.84M" },
  { lever: "Duplicate Vendor & Tail Consolidation", addressable: "$11.2M", capture: "13%", value: "$1.46M" },
  { lever: "Contract Compliance, Maverick", addressable: "$15.9M", capture: "10%", value: "$1.59M" },
  { lever: "Pre-Peak RFQ, Top 5 Categories", addressable: "$25.9M", capture: "5–9%", value: "$1.21M" },
  { lever: "Supplier Dual-Sourcing", addressable: "$16.6M", capture: "–", value: "Risk down" },
];

function KpiRow({ items }: { items: { label: string; value: string; sub: string; color: string; accent?: boolean }[] }) {
  return (
    <div className="sm-kpi-grid">
      {items.map(({ label, value, sub, color, accent }) => (
        <div key={label} className="sm-kpi" style={{ borderColor: color }}>
          <p className="sm-kpi__label">{label}</p>
          <p className={`sm-kpi__value${accent ? " sm-kpi__value--accent" : ""}`}>{value}</p>
          <p className="sm-kpi__sub">{sub}</p>
        </div>
      ))}
    </div>
  );
}

function Callout({ eyebrow, children, button }: { eyebrow: string; children: React.ReactNode; button: string }) {
  return (
    <div className="sm-callout">
      <div className="sm-callout__body">
        <p className="sm-callout__eyebrow">{eyebrow}</p>
        <p className="sm-callout__text">{children}</p>
      </div>
      <button type="button" className="sm-callout__btn sm-callout__btn--filled">
        {button}
        <ArrowRight size={14} />
      </button>
    </div>
  );
}

function CardHeader({ title, subtitle, tag }: { title: string; subtitle: React.ReactNode; tag: string }) {
  return (
    <div className="op-card-head">
      <div>
        <p className="op-card-head__title">{title}</p>
        <p className="op-card-head__sub">{subtitle}</p>
      </div>
      <span className="sm-tag">{tag}</span>
    </div>
  );
}

function WaterfallChart() {
  const W = 900;
  const H = 280;
  const left = 56;
  const top = 24;
  const bottom = 240;
  const max = 8;
  const y = (v: number) => bottom - (v / max) * (bottom - top);
  const slot = (W - left - 10) / WATERFALL.length;
  const barW = 56;

  return (
    <svg className="op-waterfall" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Savings waterfall">
      {[0, 2, 4, 6, 8].map((t) => (
        <g key={t}>
          <line x1={left} x2={W - 10} y1={y(t)} y2={y(t)} stroke="#e5e7eb" strokeDasharray="4 4" />
          <text x={left - 10} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#9ca3af">${t}M</text>
        </g>
      ))}
      {WATERFALL.map((b, i) => {
        const cx = left + slot * i + slot / 2;
        const hi = Math.max(b.from, b.to);
        const lo = Math.min(b.from, b.to);
        return (
          <g key={b.label}>
            <rect x={cx - barW / 2} y={y(hi)} width={barW} height={y(lo) - y(hi)} rx="3" fill={b.color} />
            <text x={cx} y={y(hi) - 6} textAnchor="middle" fontSize="12" fontWeight="700" fill="#1e1b4b">{b.text}</text>
            <text x={cx} y={bottom + 18} textAnchor="middle" fontSize="11" fill="#9ca3af">{b.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function PortfolioTab() {
  const [chip, setChip] = useState<ChipFilter>("All");
  const visible = OPPORTUNITIES.filter((o) => chip === "All" || o.category === chip);

  return (
    <div className="sm-panel">
      <KpiRow items={PORTFOLIO_KPIS} />
      <div className="op-chips">
        {CHIPS.map((c) => (
          <button key={c} type="button" onClick={() => setChip(c)} className={`op-chip${chip === c ? " op-chip--active" : ""}`}>
            {c}
          </button>
        ))}
      </div>
      <div className="op-card-grid">
        {visible.map((o) => (
          <article key={o.tag} className="op-opp">
            <div className="op-opp__top">
              <span className="op-opp__tag">{o.tag}</span>
              <div className="op-opp__value-wrap">
                <p className="op-opp__value">{o.value}</p>
                <p className="op-opp__value-sub">{o.valueSub}</p>
              </div>
            </div>
            <p className="op-opp__title">{o.title}</p>
            <div className="op-opp__stats">
              {[["Addressable", o.addressable], ["Capture", o.capture], ["Priority", o.priority]].map(([k, v]) => (
                <div key={k} className="op-opp__stat">
                  <p className="op-opp__stat-label">{k}</p>
                  <p className="op-opp__stat-value">{v}</p>
                </div>
              ))}
            </div>
            <button type="button" className="op-opp__trace">
              Traces from: {o.trace}
              <ArrowRight size={12} />
            </button>
            <div className="op-opp__footer">
              <span className="op-avatar" style={{ background: o.ownerColor }}>{o.initials}</span>
              <span className="op-opp__owner">{o.owner}</span>
              <span className={`op-status op-status--${o.status.toLowerCase().replace(" ", "-")}`}>{o.status}</span>
            </div>
          </article>
        ))}
      </div>
      <p className="op-footnote">Each card traces back to a source insight and forward to an owner and ETA. Filter chips are live.</p>
    </div>
  );
}

function PrioritizationTab() {
  return (
    <div className="sm-panel">
      <div className="si-card sm-card">
        <CardHeader title="Prioritization, Value × Effort" subtitle="Sequence the prize: do the high-value low-effort levers first" tag="P1 / P2" />
        <div className="op-quadrants">
          {QUADRANTS.map((q) => (
            <div key={q.key} className={`op-quadrant op-quadrant--${q.key}`}>
              <div className="op-quadrant__head">
                <div>
                  <p className="op-quadrant__title">{q.title}</p>
                  <p className="op-quadrant__sub">{q.sub}</p>
                </div>
                <p className="op-quadrant__hint">{q.hint}</p>
              </div>
              {q.items.map(([name, val]) => (
                <div key={name} className="op-quadrant__item">
                  <span>{name}</span>
                  <strong>{val}</strong>
                </div>
              ))}
            </div>
          ))}
        </div>
        <p className="op-axis-note">Value at stake (vertical) · ease of execution (horizontal)</p>
        <Callout eyebrow="From: Prioritization" button="Lock the Phase 1 plan">
          <strong>Capture $2.78M of do-first levers in Phase 1 (0–3 mo)</strong> while the $1.84M should-cost recovery is structured into the next RFQ round.
        </Callout>
      </div>
      <p className="op-footnote">Each card traces back to a source insight and forward to an owner and ETA. Filter chips are live.</p>
    </div>
  );
}

function SavingsTab() {
  return (
    <div className="sm-panel">
      <div className="si-card sm-card">
        <CardHeader
          title="Savings Engine, Identified to Net"
          subtitle={<>Levers overlap; the <span className="op-accent">waterfall nets out the double-count</span></>}
          tag="Waterfall"
        />
        <WaterfallChart />
        <table className="sm-table op-savings-table">
          <thead>
            <tr>
              <th>Lever</th>
              <th className="sm-table__right">Addressable</th>
              <th className="sm-table__right">Capture</th>
              <th className="sm-table__right">Value</th>
            </tr>
          </thead>
          <tbody>
            {LEVER_ROWS.map((r) => (
              <tr key={r.lever}>
                <td className="sm-table__strong">{r.lever}</td>
                <td className="sm-table__right op-purple">{r.addressable}</td>
                <td className="sm-table__right sm-table__muted">{r.capture}</td>
                <td className="sm-table__right sm-table__accent">{r.value}</td>
              </tr>
            ))}
            <tr className="op-savings-table__net">
              <td className="sm-table__strong">Net of overlap</td>
              <td />
              <td />
              <td className="sm-table__right sm-table__accent sm-table__strong">$4.72M</td>
            </tr>
            <tr>
              <td className="sm-table__muted">+ Working capital freed</td>
              <td />
              <td />
              <td className="sm-table__right op-teal sm-table__strong">$1.32M</td>
            </tr>
          </tbody>
        </table>
        <p className="op-footnote op-footnote--inset">
          Capture rates are editable assumptions (10–30% industry benchmarks). Working capital is one-time cash freed, tracked separately from annual P&amp;L savings.
        </p>
      </div>
    </div>
  );
}

function RecommendationsTab() {
  return (
    <div className="sm-panel">
      <KpiRow items={RECO_KPIS} />
      <div className="si-card sm-card">
        <CardHeader title="Next Best Actions" subtitle="Priority-ranked, owned, ready to assign" tag="Recommendation engine" />
        {ACTIONS.map((g) => (
          <div key={g.group} className="op-actions">
            <p className={`op-actions__group op-actions__group--${g.tone}`}>{g.group}</p>
            <div className="op-actions__list">
              {g.items.map((a) => (
                <div key={a.title} className="op-action">
                  <span className="op-action__bar" />
                  <div className="op-action__body">
                    <p className="op-action__title">{a.title}</p>
                    <p className="op-action__desc">{a.desc}</p>
                  </div>
                  <div className="op-action__meta">
                    <p className="op-action__value">{a.value}</p>
                    <p className="op-action__owner">{a.owner}</p>
                  </div>
                  <button type="button" className="op-assign">
                    Assign
                    <ArrowRight size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExecutionTab() {
  return (
    <div className="sm-panel">
      <div className="si-card sm-card">
        <div className="op-card-head">
          <div>
            <p className="op-card-head__title">Execution Tracker</p>
            <p className="op-card-head__sub">Opportunity lifecycle, <span className="op-purple">owner</span> and ETA on every card</p>
          </div>
          <span className="sm-tag">Kanban</span>
        </div>
        <div className="op-kanban">
          {KANBAN.map((col) => (
            <div key={col.title} className="op-kanban__col">
              <div className="op-kanban__head">
                <span className="op-kanban__dot" style={{ background: col.dot }} />
                <span className="op-kanban__name">{col.title}</span>
                <span className="op-kanban__count">{col.cards.length}</span>
              </div>
              {col.cards.length === 0 ? (
                <div className="op-kanban__empty">Nothing realized yet</div>
              ) : (
                col.cards.map((c) => (
                  <div key={c.title} className={`op-kcard op-kcard--${col.variant}`}>
                    <p className="op-kcard__title">{c.title}</p>
                    <p className="op-kcard__value">{c.value}</p>
                    <p className="op-kcard__meta">{c.meta}</p>
                  </div>
                ))
              )}
            </div>
          ))}
        </div>
        <Callout eyebrow="From: Execution" button="Open value realization">
          <strong>$4.62M of value is in motion</strong> across approved and in-progress; nothing has reached realized yet, the gap leadership should track weekly.
        </Callout>
      </div>

      <div className="si-card sm-card">
        <div className="op-card-head">
          <div>
            <p className="op-card-head__title">Value Realization Funnel</p>
            <p className="op-card-head__sub op-accent">Insight to realized, the platform&apos;s core accountability metric</p>
          </div>
          <span className="sm-tag">Closes the loop</span>
        </div>
        <div className="op-funnel">
          {FUNNEL.map((f) => (
            <div key={f.label} className="op-funnel__row">
              <p className="op-funnel__label">{f.label}</p>
              <div className="op-funnel__track">
                <div className="op-funnel__bar" style={{ width: `${f.pct}%`, background: f.color }}>
                  <span>{f.value}</span>
                </div>
              </div>
              <p className="op-funnel__note">{f.note}</p>
            </div>
          ))}
        </div>
        <p className="op-footnote op-footnote--inset">
          Realization rate = realized / identified. The gap between identified and realized is what this platform exists to close:{" "}
          <span className="op-purple">Insight → Recommendation → Action → Outcome.</span>
        </p>
      </div>
    </div>
  );
}

export function OpportunityInsightsView() {
  const [heroTab, setHeroTab] = useState(HERO_TABS[0]);
  const [activeTab, setActiveTab] = useState<Tab>("Opportunity Portfolio");

  return (
    <div className="si-page">
      <div className="sm-header">
        <div>
          <p className="si-header__eyebrow">Spend Insights</p>
          <h2 className="si-header__title">Opportunity &amp; Action</h2>
          <p className="si-header__description">
            Continuously generated recommendations across consolidation, leakage, tail spend, and payment optimization.
          </p>
        </div>
        <div className="sm-header__actions">
          <span className="sm-pill">
            <Sparkles size={14} />
            14 active opportunities
          </span>
          <button type="button" className="sm-pill sm-pill--outline">Generate Executive Summary</button>
        </div>
      </div>

      <section className="si-hero sm-hero op-hero">
        <div className="sm-hero__main">
          <h3 className="sm-hero__headline">
            $6.8M identified, $4.7M net after overlap. $4.6M is already in motion. Nothing is realized yet.
          </h3>
          <p className="sm-hero__sub">Leverage insights, suppliers, operations, and realize value starting this quarter.</p>
          <div className="si-hero__buttons">
            {HERO_TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setHeroTab(t)}
                className={`si-hero__chip${heroTab === t ? " si-hero__chip--active" : ""}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="si-hero__stats sm-hero__stats">
            {HERO_STATS.map(({ value, label, accent }) => (
              <div key={label}>
                <p className={`si-hero__stat-value sm-hero__stat-value${accent ? " op-hero__stat--accent" : ""}`}>{value}</p>
                <p className="si-hero__stat-label">{label}</p>
              </div>
            ))}
          </div>
          <div className="si-hero__cta-group sm-hero__cta-group">
            <button type="button" className="sm-hero__cta op-hero__cta">Close the realization gap: $4.74M</button>
            <p className="op-hero__note">available to confirm in motion items</p>
          </div>
          <div className="si-hero__synth">
            <Zap size={12} />
            <p className="si-hero__synth-text">AI synthesis · updated 4 min ago</p>
          </div>
        </div>
      </section>

      <SpendFilterSection labelClassName="sm-filter-pill__label" chevronSize={14} savedViewsIcon="bookmark" clearLabel="X Clear" />

      <section>
        <p className="si-section-eyebrow">Deep Dive by Section</p>
        <div className="si-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTab(t)}
              className={`si-tab sm-tab${activeTab === t ? " si-tab--active" : ""}`}
            >
              {t}
            </button>
          ))}
        </div>

        {activeTab === "Opportunity Portfolio" && <PortfolioTab />}
        {activeTab === "Prioritization" && <PrioritizationTab />}
        {activeTab === "Savings Engine" && <SavingsTab />}
        {activeTab === "Recommendations" && <RecommendationsTab />}
        {activeTab === "Execution & Realization" && <ExecutionTab />}
      </section>
    </div>
  );
}
